import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { LeadFormProvider } from "@/components/lead/LeadForm";
import { PortalAuthProvider } from "@/components/portal/PortalAuth";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { MobileBar } from "@/components/site/MobileBar";
import { CookieBanner } from "@/components/site/CookieBanner";
import { ChatWidget } from "@/components/site/ChatWidget";
import { products } from "@/data/products";
import FreeHomeMeasure from "@/pages/FreeHomeMeasure";
import BlindsBirmingham from "@/pages/BlindsBirmingham";
import CommercialBlinds from "@/pages/CommercialBlinds";
import ThankYou from "@/pages/ThankYou";
import AreaPage from "@/pages/AreaPage";
import Home from "@/pages/Home";
import Blinds from "@/pages/Blinds";
import ProductPage from "@/pages/ProductPage";
import Shutters from "@/pages/Shutters";
import WhyChooseUs from "@/pages/WhyChooseUs";
import OurWork from "@/pages/OurWork";
import AreasWeCover from "@/pages/AreasWeCover";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import NotFound from "@/pages/NotFound";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalLayout from "@/components/portal/PortalLayout";
import DashboardPage from "@/pages/portal/DashboardPage";
import LeadsPage from "@/pages/portal/LeadsPage";
import CustomersPage from "@/pages/portal/CustomersPage";
import CustomerRecordPage from "@/pages/portal/CustomerRecordPage";
import VisitsPage from "@/pages/portal/VisitsPage";
import QuotesPage from "@/pages/portal/QuotesPage";
import OrdersPage from "@/pages/portal/OrdersPage";
import OrderDetailPage from "@/pages/portal/OrderDetailPage";
import OrderSheetPrintPage from "@/pages/portal/OrderSheetPrintPage";
import SchedulePage from "@/pages/portal/SchedulePage";
import InstallationsPage from "@/pages/portal/InstallationsPage";
import PaymentsPage from "@/pages/portal/PaymentsPage";
import InvoicesPage from "@/pages/portal/InvoicesPage";
import InvoicePrintPage from "@/pages/portal/InvoicePrintPage";
import MaterialsPage from "@/pages/portal/MaterialsPage";
import ReportsPage from "@/pages/portal/ReportsPage";
import UsersPage from "@/pages/portal/UsersPage";
import SettingsPage from "@/pages/portal/SettingsPage";
import InstallerView from "@/pages/portal/InstallerView";
import QuotePage from "@/pages/QuotePage";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function Shell() {
  const location = useLocation();
  const bareRoute =
    location.pathname.startsWith("/portal") || location.pathname.startsWith("/quote");

  // HubSpot tracking code on every public website page (ID comes from portal Settings)
  useEffect(() => {
    if (bareRoute) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/config`)
      .then((r) => r.json())
      .then((cfg) => {
        const id = (cfg.hubspot_tracking_id || "").trim();
        if (!id || document.getElementById("hs-script-loader")) return;
        const src = id.includes("/") ? (id.startsWith("http") ? id : `https://${id}`) : `https://js.hs-scripts.com/${id}.js`;
        const s = document.createElement("script");
        s.id = "hs-script-loader";
        s.async = true;
        s.defer = true;
        s.src = src;
        document.body.appendChild(s);
      })
      .catch(() => {});
  }, [bareRoute]);

  useEffect(() => {
    if (bareRoute) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, [bareRoute]);

  return (
    <LeadFormProvider>
      <PortalAuthProvider>
        <ScrollToTop />
        {!bareRoute && <Header />}
        <main id="main-content">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/blinds" element={<Blinds />} />
            <Route path="/blinds/:slug" element={<ProductPage />} />
            <Route path="/shutters" element={<Shutters />} />
            <Route path="/why-choose-us" element={<WhyChooseUs />} />
            <Route path="/our-work" element={<OurWork />} />
            <Route path="/areas-we-cover" element={<AreasWeCover />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/free-home-measure" element={<FreeHomeMeasure />} />
            <Route path="/blinds-birmingham" element={<BlindsBirmingham />} />
            <Route path="/commercial-blinds-birmingham" element={<CommercialBlinds />} />
            <Route path="/commercial-blinds" element={<Navigate to="/commercial-blinds-birmingham" replace />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/areas/:slug" element={<AreaPage />} />
            {products
              .filter((p) => p.slug !== "shutters")
              .map((p) => (
                <Route key={p.slug} path={`/${p.slug}`} element={<ProductPage slugOverride={p.slug} />} />
              ))}
            <Route path="/privacy-policy" element={<Privacy />} />
            <Route path="/terms-and-conditions" element={<Terms />} />
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/jobs" element={<InstallerView />} />
            <Route path="/portal/invoices/:id/print" element={<InvoicePrintPage />} />
            <Route path="/portal/orders/:id/sheet" element={<OrderSheetPrintPage />} />
            <Route path="/portal" element={<PortalLayout />}>
              <Route index element={<DashboardPage />} />
              <Route path="leads" element={<LeadsPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="customers/:id" element={<CustomerRecordPage />} />
              <Route path="visits" element={<VisitsPage />} />
              <Route path="schedule" element={<SchedulePage />} />
              <Route path="quotes" element={<QuotesPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="orders/:id" element={<OrderDetailPage />} />
              <Route path="installations" element={<InstallationsPage />} />
              <Route path="payments" element={<PaymentsPage />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="materials" element={<MaterialsPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            <Route path="/quote/:id" element={<QuotePage />} />
            <Route path="/quote/:ref/:token" element={<QuotePage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        {!bareRoute && (
          <>
            <Footer />
            <MobileBar />
            <CookieBanner />
            <ChatWidget />
          </>
        )}
        <Toaster position="top-center" />
      </PortalAuthProvider>
    </LeadFormProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}

export default App;
